import React from "react";
import { useNavigate } from "react-router-dom";

export default function HeroSection({
  title,
  tagline,
  subtitle,
  outcomes = [],
  primary_cta,
  secondary_cta,
  launchMode = false,
}) {
  const navigate = useNavigate();

  const isPrimaryDisabled = launchMode || Boolean(primary_cta?.disabled);
  const isSecondaryDisabled = launchMode || Boolean(secondary_cta?.disabled);

  const handlePrimary = () => {
    if (isPrimaryDisabled) return;
    if (primary_cta?.href) navigate(primary_cta.href);
  };

  const handleSecondary = () => {
    if (isSecondaryDisabled) return;
    if (!secondary_cta?.href) return;
    const href = secondary_cta.href;
    if (href.startsWith("http") || href.endsWith(".pdf")) {
      window.open(href, "_blank", "noopener noreferrer");
    } else {
      navigate(href);
    }
  };

  return (
    <section
      style={{
        paddingTop: "var(--sx-hero-top)",
        paddingBottom: "var(--sx-hero-bottom)",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          maxWidth: 720,
          width: "100%",
          textAlign: "center",
        }}
      >
        {/* Title */}
        <h1 className="hero-title">
          <span className="brand-s">{title.brand}</span>
          <span className="brand-sx">{title.accent}</span>
          <span className="brand-s">{title.suffix}</span>
        </h1>

        {tagline && (
          <h2 style={{
            fontSize: "clamp(19px, 3vw, 24px)",
            fontWeight: 600,
            color: "#e5e7eb",
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
            marginTop: 14,
            marginBottom: 12,
          }}>
            {tagline}
          </h2>
        )}

        {subtitle && (
          <p style={{
            fontSize: 14.5,
            color: "rgba(203,213,225,0.70)",
            lineHeight: 1.70,
            marginBottom: 20,
            maxWidth: 560,
            margin: "0 auto 20px",
          }}>
            {subtitle}
          </p>
        )}

        {/* CTA Buttons */}
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {/* PRIMARY CTA */}
          {primary_cta?.label && (
            <button
              type="button"
              onClick={handlePrimary}
              disabled={isPrimaryDisabled}
              style={{
                padding: "10px 22px",
                borderRadius: 999,

                display: "inline-flex",
                alignItems: "center",
                gap: 8,

                background: isPrimaryDisabled
                  ? "rgba(245,158,11,0.16)"
                  : "#f59e0b",

                color: isPrimaryDisabled
                  ? "rgba(245,158,11,0.95)"
                  : "#020617",

                fontSize: 14,
                fontWeight: 600,

                border: isPrimaryDisabled
                  ? "1px solid rgba(245,158,11,0.35)"
                  : "none",

                cursor: isPrimaryDisabled ? "default" : "pointer",
                opacity: isPrimaryDisabled ? 0.88 : 1,
                transition: "0.25s ease",

                boxShadow: isPrimaryDisabled
                  ? "none"
                  : "0 14px 40px rgba(245,158,11,0.18)",
              }}
              onMouseEnter={(e) => {
                if (isPrimaryDisabled) return;
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0px)";
              }}
            >
              {/* 🔒 Lock icon */}
              {isPrimaryDisabled && (
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: 14,
                    lineHeight: 1,
                    opacity: 0.95,
                  }}
                >
                  🔒
                </span>
              )}

              {primary_cta.label}

              {/* Badge */}
              {isPrimaryDisabled && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "rgba(2,6,23,0.35)",
                    border: "1px solid rgba(245,158,11,0.25)",
                    color: "rgba(245,158,11,0.9)",
                  }}
                >
                  Limited Preview
                </span>
              )}
            </button>
          )}

          {/* SECONDARY CTA */}
          {secondary_cta?.label && (
            <button
              type="button"
              onClick={handleSecondary}
              disabled={isSecondaryDisabled}
              style={{
                padding: "10px 22px",
                borderRadius: 999,

                display: "inline-flex",
                alignItems: "center",
                gap: 8,

                border: isSecondaryDisabled
                  ? "1px solid rgba(148,163,184,0.25)"
                  : "1px solid rgba(148,163,184,0.4)",

                background: isSecondaryDisabled
                  ? "rgba(255,255,255,0.02)"
                  : "transparent",

                color: isSecondaryDisabled
                  ? "rgba(203,213,225,0.6)"
                  : "#e5e7eb",

                fontSize: 14,
                fontWeight: 500,

                cursor: "pointer",
                opacity: isSecondaryDisabled ? 0.82 : 1,
                transition: "0.25s ease",
              }}
            >
              {isSecondaryDisabled && (
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: 14,
                    lineHeight: 1,
                    opacity: 0.9,
                  }}
                >
                  🔒
                </span>
              )}

              {secondary_cta.label}
            </button>
          )}
        </div>

        {/* Launch message */}
        {(isPrimaryDisabled || isSecondaryDisabled) && (
          <p
            style={{
              marginTop: 14,
              fontSize: 12.5,
              color: "rgba(203,213,225,0.75)",
              lineHeight: 1.6,
            }}
          >
            Launching soon — we’re preparing the first public release.
          </p>
        )}

        {/* Outcomes — "You’ll leave knowing:" */}
        {outcomes.length > 0 && (
          <div className="sx-hero-outcomes">
            <div className="sx-hero-outcomes-label">After your report, you’ll know:</div>
            <div className="sx-hero-outcomes-list">
              {outcomes.map((item, i) => (
                <div key={i} className="sx-hero-outcome-item">
                  <span className="sx-hero-outcome-dot">✦</span>
                  <span className="sx-hero-outcome-text">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
